import { getDateRangeForAPI } from './../../../../utils/date-range';
import { useQuery } from 'react-query';
import { SalesReportSummaryTransactionResponse, SalesReportTransactionResponse } from '../types';

import { axiosInstanceShoppaBackend } from '../../../../integrations/axios.config';
import { FilterBy } from '@/models';
import { formatToISOEndOfDayLocal, formatToISOStartOfDayLocal } from '../../../../utils/formatDates';
import { DateRange } from 'react-day-picker';

type SalesReportQueryParams = {
	searchTerm?: string;
	bookmark: string | null;
	filterBy: string | null;
	endDate?: Date | undefined;
	startDate?: Date | undefined;
};

type SalesReportResponse = {
	data: {
		sales: SalesReportTransactionResponse[];
		nextPageBookmark: string | null;
		previousPageBookmark: string | null;
	};
};

export const useGetSalesReport = (params: SalesReportQueryParams) => {
	const { searchTerm, filterBy, startDate, endDate, bookmark } = params;

	const dateKey = startDate && endDate ? [`${startDate}${endDate}`] : [];

	return useQuery<SalesReportResponse>(['sales-report', searchTerm, bookmark, ...dateKey, filterBy], async () => {
		const queryParams = new URLSearchParams();

		queryParams.append('excludeOpeningBalance', 'true');

		if (searchTerm) queryParams.append('searchTerm', searchTerm);

		if (bookmark) queryParams.append('bookmark', bookmark);

		if (filterBy && (!startDate || !endDate)) {
			queryParams.append('filterBy', filterBy);
		}

		if (startDate && endDate) {
			const formattedStartDate = formatToISOStartOfDayLocal(startDate);
			queryParams.append('startDate', formattedStartDate);

			const formattedEndDate = formatToISOEndOfDayLocal(endDate);
			queryParams.append('endDate', formattedEndDate);

			queryParams.append('filterBy', '');
		}

		return await axiosInstanceShoppaBackend.get(`/sales?${queryParams.toString()}`);
	});
};

type SalesReportSummaryResponse = {
	data: {
		salesSummary: SalesReportSummaryTransactionResponse;
	};
};

type SalesReportSummaryQueryParams = {
	filterBy: FilterBy | null;
	endDate?: Date | undefined;
	startDate?: Date | undefined;
};

export const useGetSalesReportSummary = (params: SalesReportSummaryQueryParams) => {
	const { filterBy, endDate, startDate } = params;

	const dateKey = startDate && endDate ? `${startDate}${endDate}` : null;

	return useQuery<SalesReportSummaryResponse>({
		queryKey: ['sales-report-summary', filterBy, dateKey],
		queryFn: async () => {
			const queryParams = new URLSearchParams();

			if (filterBy && (!startDate || !endDate)) queryParams.append('filterBy', filterBy);

			if (startDate && endDate) {
				queryParams.append('startDate', formatToISOStartOfDayLocal(startDate));

				const formattedEndDate = new Date(endDate).toISOString();
				queryParams.append('endDate', formatToISOEndOfDayLocal(endDate));

				queryParams.append('filterBy', '');
			}
			return await axiosInstanceShoppaBackend.get(`/report/sales/summary?${queryParams.toString()}`);
		},
	});
};

export interface PaymentMethodMetrics {
	method: string;
	amount: number;
	count: number;
	percentage: number;
}

export interface SalesMetricsData {
	totalRevenue: number;
	totalTransactions: number;
	averageTransactionValue: number;
	outstandingAmount: number;
	completedTransactions: number;
	pendingTransactions: number;
	voidedTransactions: number;
	paymentMethods: PaymentMethodMetrics[];
	revenueComparison: {
		current: number;
		previous: number;
		percentage: string;
		isPositive: boolean;
	};
	transactionComparison: {
		current: number;
		previous: number;
		percentage: string;
		isPositive: boolean;
	};
}

export interface SalesMetricsResponse {
	data: SalesMetricsData;
}

type SalesMetricsParams = {
	salesFilter?: string;
	customDate?: DateRange | undefined;
};

export const useGetSalesMetrics = (params: SalesMetricsParams = {}) => {
	const { salesFilter, customDate } = params;

	return useQuery<SalesMetricsResponse>({
		queryKey: ['sales-metrics', salesFilter, customDate],
		queryFn: async () => {
			const queryParams = new URLSearchParams();

			if (salesFilter) {
				// Handle date filters by converting them to startDate and endDate
				const { startDate, endDate } = getDateRangeForAPI(salesFilter, customDate as any);
				queryParams.append('startDate', startDate);
				queryParams.append('endDate', endDate);
			}

			// Always exclude opening balance transactions from sales metrics
			queryParams.append('excludeOpeningBalance', 'true');

			return await axiosInstanceShoppaBackend.get(`/sales/metrics?${queryParams.toString()}`);
		},
		staleTime: 1000 * 60 * 5,
		refetchInterval: 1000 * 60 * 5,
	});
};
